import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import { Table, Drawer, Button } from "antd";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import { useDispatch, useSelector } from "react-redux";
import { tasksApis } from "../../../api/NewTasks";
import { ColumnType } from "antd/es/table";
import { RootState } from "../../../store/store";
import { updateTaskListDetailsModalSlice } from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import SubTaskQuickViewCard from "./SubTaskQuickViewCard";

export interface TaskTagsInterface {
  tagId: number;
  tagName: string;
  taskTagId: number;
}
export type TaskStatus =
  | "Active"
  | "Inactive"
  | "On Hold"
  | "Closed"
  | "Archived"
  | "Deleted"
  | "In Progress"
  | "Draft"
  | "Pending Approval"
  | "Approval Rejected";

export type TaskFlagEnum = "Internal" | "External";
interface TaskTag {
  tagName: string;
}

interface TaskBoq {
  activityBoqId: number;
  allotedQuantity: number;
}

interface TaskResource {
  activityResourceId: number;
}

interface TaskUser {
  projectUserId: number;
  taskUserType: "Owner" | "Assignee" | string;
}

interface TaskItem {
  projectId: number;
  phaseId: number;
  activityId: number;
  taskName: string;
  taskDescription: string;
  taskOwnerId: number;
  taskActualStartDate: string;
  taskActualEndDate: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityId: number;
  isBillable: boolean;
  workHoursInSec: number;
  taskTags: TaskTag[];
  taskBoqs: TaskBoq[];
  taskResources: TaskResource[];
  taskUsers: TaskUser[];
  uid?: any;
  [v: string]: any;
}

interface SubTaskListProps {
  isActive?: boolean;
}

const SubTaskList: FC<
  SubTaskListProps & { isFromWorkUI?: boolean; isActive?: boolean }
> = ({ isFromWorkUI, isActive = false }) => {
  const dispatch = useDispatch();

  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );

  const { selectedTaskDetails } = TaskListInDetailModal;
  const { refreshSubTaskList } = TaskListInDetailModal;

  // Use values from selectedTaskDetails/TaskListInDetailModal
  const activityIds = selectedTaskDetails?.activityId;
  const projectIds = selectedTaskDetails?.projectId;
  const phaseIds = selectedTaskDetails?.phaseId;
  const allowEdit = TaskListInDetailModal.allowEdit;

  // Store the original allowEdit value to avoid it being overwritten by subtask clicks
  const [originalAllowEdit, setOriginalAllowEdit] = useState(allowEdit);
  // Store the current subtask's edit permission
  const [currentSubtaskAllowEdit, setCurrentSubtaskAllowEdit] = useState(true);

  const location = useLocation();
  // Get location state safely
  const locationState = location.state as any;
  const locationExtraFields = locationState?.extra;
  const derivedProjectStatus = locationExtraFields?.screenName;

  const [dataSource, setDataSource] = useState<any>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [endOfList, setEndOfList] = useState(false);

  // Drawer for view-only subtask details
  const [viewDrawer, setViewDrawer] = useState<{
    open: boolean;
    subTaskId: number | null;
    allowEdit: boolean;
  }>({ open: false, subTaskId: null, allowEdit: true });

  // Update originalAllowEdit when allowEdit changes (but not when it's overwritten by subtask clicks)
  useEffect(() => {
    if (allowEdit !== undefined && !TaskListInDetailModal.isSubtaskUpdate) {
      setOriginalAllowEdit(allowEdit);
    }
  }, [allowEdit, TaskListInDetailModal.isSubtaskUpdate]);

  const columns: ColumnType<TaskItem>[] = [
    {
      title: "Task Name",
      dataIndex: "subTaskName",
      key: "subTaskName",
      width: 250,
      fixed: "left",
      render(text: any, record: TaskItem) {
        return (
          <span
            className="inlineTableCellLink"
            id={`Task-${record?.uid}`}
            onClick={() => {
              if (isFromWorkUI) return;
              const subtaskStatus = record?.subTaskStatus?.toLowerCase() || "";
              const isSubtaskEditable = ![
                "closed",
                "archived",
                "onhold",
              ].includes(subtaskStatus);
              const calculatedAllowEdit =
                isSubtaskEditable && originalAllowEdit;
              setCurrentSubtaskAllowEdit(calculatedAllowEdit);
              setViewDrawer({
                open: true,
                subTaskId: record?.subTaskId,
                allowEdit: calculatedAllowEdit,
              });
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Owner",
      dataIndex: "subTaskOwnerName",
      key: "subTaskOwnerName",
      width: 250,
    },
    {
      title: "Planned Start Date",
      dataIndex: "subTaskPlannedStartDate",
      key: "subTaskPlannedStartDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "subTaskPlannedEndDate",
      key: "subTaskPlannedEndDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Actual Start Date",
      dataIndex: "subTaskActualStartDate",
      key: "subTaskActualStartDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Actual End Date",
      dataIndex: "subTaskActualEndDate",
      key: "subTaskActualEndDate",
      width: 250,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Priority",
      dataIndex: "priorityName",
      key: "priorityName",
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "subTaskStatus",
      key: "subTaskStatus",
      width: 200,
      fixed: "right",
    },
  ];

  const methods = useForm({
    mode: "onChange",
  });

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;

      const apiResponse: any = await tasksApis.subTasksList({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds,
        phaseIds,
        activityIds,
        taskIds: selectedTaskDetails?.taskId,
        derivedProjectStatus: derivedProjectStatus,
      });

      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count, taskResponse } =
          data || apiResponse?.response.data || apiResponse.response;

        if (taskResponse) {
          dispatch(
            updateTaskListDetailsModalSlice({
              taskResponse: taskResponse,
            })
          );
        }
        if (list.length < pageSize) {
          setEndOfList(true);
        }
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current ? "F" : "B";
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.taskId
            : dataSource[0]?.taskId;
        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const handleAddNewSubtask = () => {
    if (isFromWorkUI) return;
    dispatch(
      updateTaskListDetailsModalSlice({
        isSubtask: true,
        showTaskCreationModal: true,
        selectedSubtaskDetails: null,
        isSubtaskUpdate: false,
        currentSubtaskId: null,
      })
    );
  };

  useEffect(() => {
    if (selectedTaskDetails?.taskId && isActive) {
      fetchData({
        oldPage: 1,
        current: pagination.current.toString(),
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff: pagination.current - 1,
        paginationId: null,
        searchQuery,
        dateRange: "Quarter to Date",
      });
    }
  }, [
    selectedTaskDetails?.taskId,
    pagination.current,
    pagination.pageSize,
    searchQuery,
    isActive,
  ]);

  useEffect(() => {
    if (isActive && refreshSubTaskList) {
      // Reset to first page when refreshing
      setPagination((prev) => ({ ...prev, current: 1 }));

      fetchData({
        oldPage: 1,
        current: "1",
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        searchQuery,
        dateRange: "Quarter to Date",
      }).then(() => {
        dispatch(
          updateTaskListDetailsModalSlice({
            refreshSubTaskList: false,
          })
        );
      });
    }
  }, [refreshSubTaskList, isActive]);

  // Additional effect to refresh data when tab becomes active and there's a pending refresh
  useEffect(() => {
    if (isActive && refreshSubTaskList && selectedTaskDetails?.taskId) {
      fetchData({
        oldPage: 1,
        current: "1",
        sortOrder: "DESC",
        pageSize: pagination.pageSize.toString(),
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        searchQuery,
        dateRange: "Quarter to Date",
      });
    }
  }, [isActive, selectedTaskDetails?.taskId]);

  return (
    <>
      <FormProvider {...methods}>
        <form>
          {!isFromWorkUI && (
            <div
              className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-end align-items-center"
              style={{ minHeight: "40px", border: "0px", width: "100%" }}
            >
              <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                <li className="pt-0 pb-0">
                  <AddAccess moduleName="Tasks">
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleAddNewSubtask}
                      disabled={
                        !allowEdit ||
                        ["closed", "archived", "onhold"].includes(
                          selectedTaskDetails?.taskStatus?.toLowerCase() || ""
                        )
                      }
                    >
                      <Icon name={"plus"} />
                      <span>Add Subtask</span>
                    </Button>
                  </AddAccess>
                </li>
              </ul>
            </div>
          )}
        </form>
      </FormProvider>

      <div className="card-inner card-inner-lg p-0">
        <div className="content clearfix">
          <div
            style={{
              margin: "10px auto",
            }}
          >
            <SkeletonTable
              loading={loading}
              columns={columns as SkeletonTableColumnsType[]}
            >
              <Table
                rowKey="uid"
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={tableOnChange}
                bordered
                size="middle"
                scroll={{
                  x: 1100,
                }}
              />
            </SkeletonTable>
          </div>
        </div>
      </div>

      {/* Subtask View Drawer */}
      <Drawer
        title="Subtask Details"
        placement="right"
        width={920}
        open={viewDrawer.open}
        onClose={() =>
          setViewDrawer({ open: false, subTaskId: null, allowEdit: true })
        }
        styles={{ body: { padding: 0 } }}
        destroyOnClose
        extra={
          viewDrawer.allowEdit && !isFromWorkUI ? (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                const id = viewDrawer.subTaskId;
                const allow = viewDrawer.allowEdit;
                setViewDrawer({
                  open: false,
                  subTaskId: null,
                  allowEdit: true,
                });
                dispatch(
                  updateTaskListDetailsModalSlice({
                    isSubtaskUpdate: true,
                    isSubtask: true,
                    showTaskCreationModal: true,
                    currentSubtaskId: id,
                    subtaskAllowEdit: allow,
                    subtaskOpenInEdit: true,
                  })
                );
              }}
            >
              Edit
            </Button>
          ) : null
        }
      >
        {viewDrawer.subTaskId ? (
          <SubTaskQuickViewCard
            subTaskId={viewDrawer.subTaskId}
            derivedProjectStatus={derivedProjectStatus}
          />
        ) : null}
      </Drawer>
    </>
  );
};

export default SubTaskList;
